import type { UserOut } from "@/types/api";

// Set from AuthContext whenever the logged-in user is known, so the plain
// (non-React) repo/sync-engine functions can stamp and filter pending
// records by user without threading a param through every call site. This
// matters on a shared device: without it, User A's unsynced climbs could get
// submitted under User B's session if B logs in before A's queue drains.
let currentUserId: number | null = null;
// Bumped whenever the signed-in user changes, including to nobody, so a sync
// run can tell that a logout happened even if the same user signed back in.
let signInGeneration = 0;

export function setCurrentUserId(id: number | null): void {
  if (id !== currentUserId) signInGeneration++;
  currentUserId = id;
}

export function getSignInGeneration(): number {
  return signInGeneration;
}

export function getCurrentUserId(): number | null {
  return currentUserId;
}

const LAST_KNOWN_USER_KEY = "sendstats:lastKnownUser";

// A cold app launch with no connection still needs `getMe()` to resolve to
// *something*, or ProtectedRoute bounces the user to /login before they can
// log anything — exactly the crag/gym scenario this feature exists for. This
// cache is deliberately just the last successfully-fetched user, not a
// generic offline read cache: it exists only so a network failure isn't
// mistaken for a real logout.
export function cacheLastKnownUser(user: UserOut): void {
  try {
    localStorage.setItem(LAST_KNOWN_USER_KEY, JSON.stringify(user));
  } catch {
    // best-effort — private browsing / storage quota shouldn't break login
  }
}

export function getLastKnownUser(): UserOut | null {
  try {
    const raw = localStorage.getItem(LAST_KNOWN_USER_KEY);
    return raw ? (JSON.parse(raw) as UserOut) : null;
  } catch {
    return null;
  }
}

export function clearLastKnownUser(): void {
  try {
    localStorage.removeItem(LAST_KNOWN_USER_KEY);
  } catch {
    // ignore
  }
}
